import React, {useState} from 'react';
import {TscStyles, TscThemeName, TscThemeNames, ControlButton, Glyphs, TscButtonType} from '@techsmith/tsc-cloud-style-guide';
import styled from 'styled-components';
import {useTranslation} from 'react-i18next';
import {useTscTheme} from '../../context/themeProvider';

const ResolveButton = styled(ControlButton)<{isResolved: boolean; isSelected: boolean; theme: TscThemeNames}>`
   width: 24px;
   height: 24px;
   margin-left: 0.5rem;
   ${props => !props.isResolved && `
      fill: ${props.theme === TscThemeName.dawn ? TscStyles.color.text.lightMedium : TscStyles.color.text.medium};
      background-color: ${props.theme === TscThemeName.dawn ?
         (props.isSelected ? TscStyles.color.ui.dawn.lightMedium : TscStyles.color.ui.dawn.light) :
         (props.isSelected ? TscStyles.color.ui.dusk.medium : TscStyles.color.action.secondaryDusk)};
   `}
`;

const ResolveView = styled.div<{theme: TscThemeNames; isResolved: boolean}>`
   width: 24px;
   height: 24px;
   margin-left: 0.5rem;
   display: flex;
   align-items: center;
   justify-content: center;
   border-radius: ${TscStyles.border.radius.md};
   background-color: ${props => props.isResolved ? TscStyles.color.action.primary : props.theme === TscThemeName.dawn ? TscStyles.color.action.secondaryDawn : TscStyles.color.action.secondaryDusk};
   fill: ${props => props.isResolved ? TscStyles.color.text.light : props.theme === TscThemeName.dusk ? TscStyles.color.text.mediumDark : TscStyles.color.text.medium};
`;

const CommentResolve: React.FC<IResolveButtonProps> = ({userCanResolve, isResolved, isSelected, onToggleResolution}) => {
   const [isSaving, setIsSaving] = useState(false);
   const {t} = useTranslation();
   const theme = useTscTheme();

   const onClick = async () => {
      if (isSaving) {
         return;
      }

      setIsSaving(true);
      await onToggleResolution();
      setIsSaving(false);
   };

   return userCanResolve ? (
      <ResolveButton
         onClick={onClick}
         aria={{'aria-pressed': isResolved}}
         title={t('conversations.resolveTitle')}
         glyph={<Glyphs.Checkmark16x16 />}
         testId="resolve-comment-button"
         buttonType={isResolved ? TscButtonType.primary : TscButtonType.secondary}
         themeName={theme}
         isResolved={isResolved}
         isSelected={isSelected}
         theme={theme}
      />
   ) : (
      <ResolveView
         theme={theme}
         isResolved={isResolved}
         title={isResolved ? t('conversations.resolved') : t('conversations.notResolved')}
         data-test-id="resolve-comment-view"
      >
         <Glyphs.Checkmark16x16 />
      </ResolveView>
   );
};

export interface IResolveButtonProps {
   userCanResolve: boolean;
   isResolved: boolean;
   isSelected: boolean;
   onToggleResolution: () => Promise<void>;
}

export default CommentResolve;